import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPseudoCheckboxModule } from '@angular/material/core';

import { AbookFilterChipComponent } from './abook-filter-chip.component';

export interface FilterOption<T extends string | number = number> {
  value: T;
  label: string;
  /** An inactive anlæg: listed last and marked "(inaktiv)" (R12). */
  inactive?: boolean;
  /** A heading the option sits under, e.g. the område of a facilitet. Options of a group are adjacent. */
  group?: string;
}

/**
 * A filter chip with a list: single choice (check mark, closes on pick) or several (checkboxes,
 * stays open, R7). The chip shows "Navn · værdi" — "Første +n" for several, {@link allLabel} for
 * none. "Alle …" at the bottom of a multi list clears the choice.
 *
 * Values in and out are plain; the page keeps them in its `FilterState`:
 *
 * ```html
 * <abook-filter-select icon="location_on" label="Anlæg" allLabel="Alle anlæg" multiple
 *   [options]="locationOptions()" [value]="filters.value('anlaeg')()"
 *   [changed]="filters.isChanged('anlaeg')()"
 *   (valueChange)="filters.set('anlaeg', $event)" (cleared)="filters.reset('anlaeg')" />
 * ```
 */
@Component({
  selector: 'abook-filter-select',
  imports: [AbookFilterChipComponent, MatIconModule, MatMenuModule, MatPseudoCheckboxModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-changed]': 'changed()' },
  template: `
    <abook-filter-chip
      [icon]="icon()"
      [label]="label()"
      [value]="valueText()"
      [changed]="changed()"
      [compact]="compact()"
      [iconOnly]="iconOnly()"
      [required]="required()"
      [requiredText]="requiredText()"
      [invalid]="invalid()"
      [menu]="menu"
      (cleared)="cleared.emit()"
    />

    <mat-menu #menu="matMenu" class="abook-filter-menu" [overlapTrigger]="false" xPosition="after">
      @if (listHeading()) {
        <div class="abook-filter-menu__heading" role="presentation">{{ listHeading() }}</div>
      }
      @if (multiple()) {
        @for (option of sortedOptions(); track option.value; let i = $index) {
          @if (startsGroup(i)) {
            <div class="abook-filter-menu__heading" role="presentation">{{ option.group }}</div>
          }
          <button
            mat-menu-item
            role="menuitemcheckbox"
            [attr.aria-checked]="isSelected(option.value)"
            (click)="$event.stopPropagation(); toggle(option.value)"
          >
            <mat-pseudo-checkbox [state]="isSelected(option.value) ? 'checked' : 'unchecked'" />
            <span>{{ optionLabel(option) }}</span>
          </button>
        }
        <div class="abook-filter-menu__footer">
          <button mat-menu-item class="abook-filter-menu__all" (click)="chooseAll()">
            {{ allLabel() }}
          </button>
        </div>
      } @else {
        @if (allowAll()) {
          <button
            mat-menu-item
            role="menuitemradio"
            [attr.aria-checked]="selected().length === 0"
            (click)="valueChange.emit(null)"
          >
            <mat-icon [class.is-hidden]="selected().length !== 0">check</mat-icon>
            <span>{{ allLabel() }}</span>
          </button>
        }
        @for (option of sortedOptions(); track option.value; let i = $index) {
          @if (startsGroup(i)) {
            <div class="abook-filter-menu__heading" role="presentation">{{ option.group }}</div>
          }
          <button
            mat-menu-item
            role="menuitemradio"
            [attr.aria-checked]="isSelected(option.value)"
            (click)="valueChange.emit(option.value)"
          >
            <mat-icon [class.is-hidden]="!isSelected(option.value)">check</mat-icon>
            <span>{{ optionLabel(option) }}</span>
          </button>
        }
      }
    </mat-menu>
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
})
export class AbookFilterSelectComponent<T extends string | number = number> {
  readonly icon = input.required<string>();
  readonly label = input.required<string>();
  readonly options = input.required<readonly FilterOption<T>[]>();
  /** One value (`null` = Alle) or, with {@link multiple}, a list (empty = Alle). */
  readonly value = input<T | null | readonly T[]>(null);
  readonly multiple = input(false, { transform: booleanAttribute });
  /** What the chip says with nothing chosen, and the "Alle"-entry: "Alle anlæg", "Alle statusser". */
  readonly allLabel = input('Alle');
  /** Whether single choice offers "Alle" at all. Off for a filter that always has a value. */
  readonly allowAll = input(true, { transform: booleanAttribute });
  readonly changed = input(false, { transform: booleanAttribute });
  readonly compact = input(false, { transform: booleanAttribute });
  /** The chip as its icon alone ({@link AbookFilterChipComponent.iconOnly}). */
  readonly iconOnly = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly requiredText = input('Vælg');
  /** Red border after a submit with nothing chosen (the search variant, #880). */
  readonly invalid = input(false, { transform: booleanAttribute });
  /** How many chosen labels the chip names before "+n": 2 gives "Ny, Gennemført". */
  readonly maxListed = input(1);
  /** A line above the list, e.g. "Aktiviteter på Arena Vejle" for a filter that depends on another. */
  readonly listHeading = input('');

  /** `T | null` for single choice, `T[]` for {@link multiple}. */
  readonly valueChange = output<T | null | T[]>();
  readonly cleared = output<void>();

  protected readonly selected = computed<readonly T[]>(() => {
    const v = this.value();
    return v === null ? [] : Array.isArray(v) ? v : [v as T];
  });

  /** Active first, in the page's order; inactive last (R12). */
  protected readonly sortedOptions = computed(() => {
    const options = this.options();
    return [...options.filter((o) => !o.inactive), ...options.filter((o) => o.inactive)];
  });

  protected readonly valueText = computed(() => {
    const selected = this.selected();
    if (selected.length === 0) return this.required() ? '' : this.allLabel();
    const labelOf = (value: T) => {
      const option = this.options().find((o) => o.value === value);
      return option ? this.optionLabel(option) : String(value);
    };
    const listed = selected.slice(0, Math.max(1, this.maxListed())).map(labelOf).join(', ');
    const rest = selected.length - Math.max(1, this.maxListed());
    return rest > 0 ? `${listed} +${rest}` : listed;
  });

  /** Whether option `i` is the first of its group, so a heading goes above it. */
  protected startsGroup(i: number): boolean {
    const options = this.sortedOptions();
    const group = options[i].group;
    return !!group && options[i - 1]?.group !== group;
  }

  protected isSelected(value: T): boolean {
    return this.selected().includes(value);
  }

  protected optionLabel(option: FilterOption<T>): string {
    return option.inactive ? `${option.label} (inaktiv)` : option.label;
  }

  /**
   * "Alle …" at the bottom of a multi list. Normally that is no choice at all; on a required filter
   * (Balance's anlæg) no choice is not allowed, so it ticks every option instead.
   */
  protected chooseAll(): void {
    this.valueChange.emit(this.required() ? this.sortedOptions().map((o) => o.value) : []);
  }

  protected toggle(value: T): void {
    const selected = this.selected();
    this.valueChange.emit(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  }
}
